import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "./cn";

/**
 * Variantes do botão, no mesmo formato do CRM (`class-variance-authority`).
 *
 * `cva` em vez de um `Record<variante, string>` porque ele compõe variantes
 * independentes — variante × tamanho — sem multiplicar as combinações à mão, e
 * exporta o tipo das opções, então o compilador recusa `variante="azul"`.
 */
export const variantesDoBotao = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full",
    "text-sm font-medium transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-50",
    // Ícone do lucide dentro do botão: tamanho e alinhamento por padrão, para
    // cada tela não precisar ajustar.
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ],
  {
    variants: {
      variante: {
        primario: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        secundario: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        contorno:
          "border border-input bg-card shadow-sm hover:bg-accent hover:text-accent-foreground",
        fantasma: "hover:bg-accent hover:text-accent-foreground",
        perigo:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        "perigo-contorno":
          "border border-destructive bg-card text-destructive hover:bg-destructive/5",
        elo: "text-foreground underline-offset-4 hover:underline",
      },
      tamanho: {
        pequeno: "h-8 rounded-sm px-2.5",
        medio: "h-10 px-4 py-2",
        grande: "h-11 px-8 text-base",
        icone: "size-10",
      },
    },
    defaultVariants: {
      variante: "primario",
      tamanho: "medio",
    },
  },
);

export type VarianteDeBotao = NonNullable<
  VariantProps<typeof variantesDoBotao>["variante"]
>;
export type TamanhoDeBotao = NonNullable<
  VariantProps<typeof variantesDoBotao>["tamanho"]
>;

export type PropsDoBotao = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "className"
> &
  VariantProps<typeof variantesDoBotao> & {
    readonly carregando?: boolean | undefined;
    /** Renderiza no elemento filho (um `<a>`, por exemplo) em vez de `<button>`. */
    readonly asChild?: boolean | undefined;
    readonly children: ReactNode;
    readonly className?: string | undefined;
  };

/**
 * Botão do design system.
 *
 * Antes de criar um botão no seu módulo, verifique se uma variante daqui resolve
 * (`npm run inv buscar botao`). Botão local duplicado é o primeiro sintoma de
 * divergência visual entre telas.
 *
 * `carregando` desabilita e marca `aria-busy`: sem isso o usuário clica duas
 * vezes e a escrita acontece duas vezes.
 */
export function Botao({
  variante,
  tamanho,
  carregando = false,
  asChild = false,
  disabled,
  children,
  className,
  type = "button",
  ...resto
}: PropsDoBotao) {
  const Componente = asChild ? Slot : "button";

  return (
    <Componente
      {...resto}
      type={type}
      disabled={disabled === true || carregando}
      aria-busy={carregando}
      className={cn(variantesDoBotao({ variante, tamanho }), className)}
    >
      {carregando ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
      {children}
    </Componente>
  );
}
